import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { WorkflowCategoryV1Read, WorkflowSubmissionData, WorkflowV1Read } from '@ca/core/models';
import { UserNotificationService, UserWorkspaceService, WorkflowV1Service } from '@ca/core/services';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, switchMap } from 'rxjs/operators';
import { CustomValidators } from '../../../../utils/custom-validators/custom-validators';
import { DESCRIPTION_LENGTH_LIMIT, SUMMARY_LENGTH_LIMIT, TITLE_LENGTH_LIMIT } from '../../constants/form-validators';

@Component({
    selector: 'ca-workflow-publication',
    templateUrl: './workflow-publish.component.html',
    styleUrls: ['./workflow-publish.component.scss'],
})
export class WorkflowPublishComponent implements OnInit {
    @Input() categories: WorkflowCategoryV1Read[];

    readonly descriptionLengthLimit: number = DESCRIPTION_LENGTH_LIMIT;
    readonly summaryLengthLimit = SUMMARY_LENGTH_LIMIT;
    readonly titleLengthLimit = TITLE_LENGTH_LIMIT;

    formGroup: UntypedFormGroup;
    workflow: WorkflowV1Read;
    isSubmitting = false;

    get description(): AbstractControl {
        return this.formGroup.get('description') as AbstractControl;
    }

    constructor(
        private formBuilder: UntypedFormBuilder,
        private workflowV1Service: WorkflowV1Service,
        private userWorkspaceService: UserWorkspaceService,
        private userNotificationService: UserNotificationService,
        private activeModal: NgbActiveModal,
    ) {}

    ngOnInit(): void {
        this.buildForm();
    }

    submit(): void {
        if (this.workflow && this.formGroup.valid) {
            this.isSubmitting = true;
            this.userNotificationService.showSpinner();

            const { title, description, commentToReviewer, categoryId, summary } = this.formGroup.value;

            const payload: WorkflowSubmissionData = {
                title,
                description,
                summary,
                commentToReviewer,
                categoryId,
            };

            this.workflowV1Service
                .publishWorkflow(this.workflow.id, payload)
                .pipe(
                    switchMap(() => this.workflowV1Service.getById(this.workflow.id)),
                    finalize(() => {
                        this.isSubmitting = false;
                        this.userNotificationService.hideSpinner();
                    }),
                )
                .subscribe(
                    (result: WorkflowV1Read) => {
                        this.activeModal.close(result);
                        this.userNotificationService.showSuccess('WORKFLOWS.PUBLICATION.WORKFLOW_SUBMITTED_SUCCESSFULLY');
                        // Refresh the my workflow page list avoid the workflow banner refresh twice when toggle the workspace tab.
                        // @Todo: maybe this should only be done within "workspace" route ??
                        this.userWorkspaceService.updateMyWorkflows$().subscribe();
                    },
                    () => {
                        this.userNotificationService.showError('WORKFLOWS.PUBLICATION.FAILED_TO_SUBMIT_YOUR_WORKFLOW');
                    },
                );
        } else {
            this.formGroup.markAllAsTouched();
        }
    }

    dismiss(reason?: string): void {
        this.activeModal.dismiss(reason);
    }

    onTouched(): void {
        this.description.markAsTouched();
    }

    private buildForm(): void {
        if (!this.workflow) {
            return;
        }

        this.formGroup = this.formBuilder.group({
            title: [this.workflow.title, CustomValidators.ModelTitle()],
            description: [
                this.workflow.description,
                [CustomValidators.requiredWithoutHtmlTags(), CustomValidators.maxLengthWithoutHtmlTags(DESCRIPTION_LENGTH_LIMIT)],
            ],
            summary: [this.workflow.summary, CustomValidators.SummaryValidation()],
            commentToReviewer: ['', [Validators.required, Validators.maxLength(this.descriptionLengthLimit)]],
            categoryId: [this.workflow?.category?.id, Validators.required],
        });
    }
}
